import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, pluck, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthenticatedUser } from 'src/app/shared/models';
import { LoginResponse } from 'src/app/shared/models/login-response';
import { TokenDetails } from '../../models/token-details';
import { LOCAL } from '../storage/local.enum';
import { StorageService } from '../storage/storage.service';
import { UserService } from '../user/user.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { KeycloakService } from 'keycloak-angular';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements OnDestroy {

  useMockUsersFlag = false;

  tempCheckEnviroment = environment.accesGroupsPrefix;//TODO: prod is empty, dev is "DEV", 'HML'

  public get currentUser(): AuthenticatedUser | null {
    if (this.user) {
      return this.user.value;
    }
    return null;
  }

  public get email(): string {
    return this.storageService.getItem(LOCAL.EMAIL);
  }

  public get isOperator(): boolean {
    if (this.currentUser && this.currentUser.entityTypeSk === 'OPERATOR') {
      return true;
    }

    return false;
  }

  public get operator(): string {
    const user = this.storageService.getItem(LOCAL.ROLE_USER);
    return this.validateGroups(user);
  }

  private validateGroups(user) {
    let group;
    if(this.useMockUsersFlag){
      group = this.useMockUsers(user);//TODO: this is the temporary solution we need integrate with login return groups
    } else {
      group = this.buildUserGroup();
    }
    this.storageService.setItem(LOCAL.GROUP_USER, group);
    return group;
  }

  private buildUserGroup() {

    let resultGroups = '';

    let groups = this.storageService.getItem(LOCAL.RESPONSE_GROUP_USER);

    if(groups){
      groups.forEach(data => {
        if(data === `GRP_PRM${this.tempCheckEnviroment}_PARTNERHUB_PORTAL_CONSULT`){
          resultGroups += 'CONSULT';
        }
        if(data === `GRP_PRM${this.tempCheckEnviroment}_PARTNERHUB_PORTAL_CONSULT_TWT`){
          resultGroups += 'CONSULT';
        }
        if(data === `GRP_PRM${this.tempCheckEnviroment}_PARTNERHUB_PORTAL_REQUEST`){
          resultGroups += 'CONSULT REQUEST TIMWE';
        }
        if(data === `GRP_PRM${this.tempCheckEnviroment}_PARTNERHUB_PORTAL_APPROVER`){
          resultGroups += 'CONSULT APPROVE';
        }
        if(data === `GRP_PRM${this.tempCheckEnviroment}_PARTNERHUB_PORTAL_ADMIN`){
          resultGroups += 'CONSULT REQUEST ADMIN TIMWE';
        }
        if(data === `GRP_PRM${this.tempCheckEnviroment}_PARTNERHUB_PORTAL_TWT_DEV_ADMIN`){
          resultGroups += 'CONSULT REQUEST ADMIN TIMWE';
        }
      });
    } else {
      resultGroups = this.storageService.getItem(LOCAL.GROUP_USER);
    }
    return resultGroups;

  }

  public logoutAsync(): void {
    this.clearAllTokenInformation();
    this.user.next(null);

    this.router.navigate(['login']);

     this.logout().subscribe()
  }

  private useMockUsers(user) {
    const mockUsers = [
      'vmbinding',
      'ldap.user',
      'mafalda.martins',
      'pedro.europeu',
      'tiago.ferrreira',
      'kaneson.sabino',
      'claudio.paulo',
      'joao.mangana',
      'tauan.lacerda',
      'carlos.salgado',
      'joao.santos',
      'daniel.coimbra',
      'joao.p.almeida',
      'eduardo.cordeiro',
      'ayslan.marques',
      'diogo.souza',
      'luisa.silva',
      'jose.ribeiro',
      'andre.l.lopes',
      'veronica.viana',
      'david.sandoval',
      'A0116949',
    ];
    let group =  mockUsers.includes(user) ? 'CONSULT REQUEST ADMIN TIMWE' : 'VIVO'
    if(user === 'test_user_1') {
      group = 'CONSULT';
    }
    if(user === 'test_user_2') {
      group = 'CONSULT REQUEST';
    }
    if(user === 'test_user_3') {
      group = 'CONSULT APPROVE';
    }
    if(user === 'test_user_4') {
      group = 'CONSULT REQUEST ADMIN TIMWE';
    }
    if(user === 'test_user_5') {
      group = 'CONSULT REQUEST ADMIN TIMWE';
    }
    return group;
  }

  public getGroups() {
    return this.http.get<any>('/venus-api/ldap/groups')
    .pipe(
      tap(
        data => console.log(data),
        error => console.log("tap error", error)
      )
    );
  }

  public checkGroupsStr(role, isArray=false) {
    const group = this.operator ? this.operator : [];
    if(isArray){
      let val = false;
      role.forEach(data => {
        if(group.includes(data)){
          val = true;
        }
      })
      return val;
    }
    return group.includes(role);
  }

  get token(): string {
    return this.storageService.getItem(LOCAL.TOKEN);
  }

  get jwtToken(): string {
    return this.storageService.getItem(LOCAL.JWTOKEN);
  }

  get rememberMe(): string {
    return this.storageService.getItem(LOCAL.REMEMBER_ME);
  }

  get user$(): Observable<AuthenticatedUser | null> {
    return this.user.asObservable().pipe(takeUntil(this.complete$));
  }

  private readonly complete$ = new Subject();

  readonly user: BehaviorSubject<AuthenticatedUser | null>;

  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly keycloak: KeycloakService,
    private readonly toastService: ToastrService
  ) 
  {
    let userObject = null;

    userObject = this.storageService.getItem(LOCAL.CURRENT_USER);

    if (userObject === null) {
      this.storageService.removeItem(LOCAL.CURRENT_USER);
    }

    this.user = new BehaviorSubject(userObject);

    // if (this.user.value && this.user.value.entityTypeSk === 'PARTNER') {
    //   this.changeTheme(environment.colorPrimaryPartner, environment.colorSecondaryPartner);
    // }
  }

  private changeTheme(primaryColor: string, secondaryColor: string): void {
    // DESIGN: Color no longer changes according to user profile
    // document.documentElement.style.setProperty('--primary-color', primaryColor);
    // document.documentElement.style.setProperty('--secondary-color', secondaryColor);
  }

  public getTokenDetails(token: string): Observable<TokenDetails> {
    const url = `/api/webapp-partnerhub-admin/users/token-details/${token}`;

    return this.http.get<TokenDetails>(url).pipe(pluck('responseData'));
  }

  public login(data: { email: string; password: string; rememberMe: boolean }, userData = null): Observable<boolean> {
    
    const url = `/api/webapp-partnerhub-admin/users/login`;

    if (data.rememberMe) {
      this.storageService.setItem(LOCAL.EMAIL, data.email);
    } else {
      this.storageService.removeItem(LOCAL.EMAIL);
    }

    this.storageService.setItem(LOCAL.REMEMBER_ME, data.rememberMe);

    return this.http
      .post<boolean>(url, { email: data.email, password: data.password })
      .pipe(
        pluck('responseData'),
        tap((response: LoginResponse) => {
          if (response) {
            this.storageService.setItem(LOCAL.JWTOKEN, response.jwtToken);
            this.storageService.setItem(LOCAL.TOKEN, response.token);
          }
        }),
        switchMap(value =>
          value
            ? this.userService.getUserDetails().pipe(
                map(user => {
                  let myUser = user;
                  myUser.name = userData;
                  myUser.email = userData;
                  this.user.next(myUser);
                  
                  this.storageService.setItem(LOCAL.CURRENT_USER, user);

                  // if (this.user.value && this.user.value.entityTypeSk === 'PARTNER') {
                  //   this.changeTheme(environment.colorPrimaryPartner, environment.colorSecondaryPartner);
                  // }

                  return true;
                })
              )
            : of(false)
        )
      );
  }

  public loginLDAP(data: { email: string; password: string; rememberMe: boolean }){
    let formData: any = new FormData();
    formData.append("username", data.email);
    formData.append("password", data.password);
    return this.http.post<any>('/venus-api/login', formData, { withCredentials: true })
    .pipe(
      tap( // Log the result or error
        data => console.log('debug (loginLDAP):',data),
        error => console.error("tap error", error)
      )
    );
  }

  public async decodeToken() {
    const _decodeToken = (token) => {
      try {
        return JSON.parse(atob(token));
      } catch {
        return;
      }
    };
    return (await this.keycloak.getToken())
      .split('.')
      .map(token => _decodeToken(token))
      .reduce((acc, curr) => {
        if (!!curr) acc = { ...acc, ...curr };
        return acc;
      }, Object.create(null));
  }

  public getUserRoles(){
    const url = `/${environment.serverApiBaseUrl}/roles-api/list/my-roles`;
    return this.http.get<any>(url);
  }

  userData(data, group=[]){
    let myUser = {
      address: null,
      email: this.keycloak.getUsername(),
      entityId: null,
      entityTypeSk: null,
      fax: null,
      jobTitle: null,
      mobilePhone: null,
      name: this.keycloak.getUsername(),
      notes: null,
      profileId: null,
      telephone: null,
      userId: null,
      username: null
    };
    this.user.next(myUser);
    this.decodeToken().then(data => {
      this.storageService.setItem(LOCAL.SESSID, data.sid);
    });        
    this.storageService.setItem(LOCAL.CURRENT_USER, myUser);
    this.storageService.setItem(LOCAL.ROLE_USER, this.keycloak.getUsername());
    this.storageService.setItem(LOCAL.RESPONSE_GROUP_USER, group);

    //check feature flag
    this.storageService.getFeatureFlag().subscribe(data => {
      this.storageService.setItem("feature-flag", data ? data : "");
    });
  }

  public logout(): Observable<object>  {
    this.storageService.removeItem(LOCAL.CURRENT_USER);
    this.user.next(null);

    this.router.navigate(['/login']);

    return this.http.post('/venus-api/logout', { }).pipe(tap(() => this.clearAllTokenInformation())); 
  }

  public clearallCookies() {
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
  }

  async clearAllTokenInformation(){
    this.clearallCookies();
    this.storageService.clearAll();
  }

  public ngOnDestroy(): void {
    this.logout();

    this.complete$.next();

    this.complete$.complete();
  }

  public recoverPassword(email: string): Observable<any> {
    const url = `/api/webapp-partnerhub-admin/users/recover-password`;

    return this.http.post<any>(url, email);
  }

  public setPassword(token: string, password: string): Observable<{ passwordChanged: boolean }> {
    const url = `/api/webapp-partnerhub-admin/users/change-password`;

    return this.http.put<{ passwordChanged: boolean }>(url, { token, password });
  }

  public tokenExpired(token: string) {
    return false; //remove this line when finally implemented on BE
    const expiry = JSON.parse(atob(token.split('.')[1])).exp;
    return Math.floor(new Date().getTime() / 1000) >= expiry;
  }
}
